import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@/components/ui/button";
import { X as XIcon, MousePointerClick, Trash2, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

// Worker PDF.js via CDN pinnato alla versione installata (react-pdf 10.4.x)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const PDF_OPTIONS = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
};

/**
 * SignaturePlacementCanvas
 * Componente visuale per posizionare segnaposti su un PDF con vista SCORREVOLE
 * (tutte le pagine visibili in verticale, come un lettore PDF classico).
 *  - click su una pagina → aggiunge un box nel punto cliccato (page-aware)
 *  - drag → sposta
 *  - handle in basso a destra → ridimensiona
 *  - × sul box → rimuove
 *
 * Props:
 *  - file: File | Blob (PDF)
 *  - value: array di placements normalizzati [{page, x, y, w, h}] (0..1)
 *  - onChange: fn(newValue)
 *  - labelFor: fn(placement) → label per il box
 */
export default function SignaturePlacementCanvas({ file, value, onChange, testid = "sig-placement-canvas", labelFor, readOnly = false }) {
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(700);
  const [zoom, setZoom] = useState(1.0);
  const [loadError, setLoadError] = useState(null);
  const containerRef = useRef(null);
  // Ref map: {pageNumber: DOM element del wrap-pagina}
  const pageWrapRefs = useRef({});
  const dragStateRef = useRef(null);

  // react-pdf richiede una reference STABILE del prop file, altrimenti ricarica in loop
  const [pdfData, setPdfData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setPdfData(null);
    if (!file) return () => {};
    file.arrayBuffer()
      .then((buf) => { if (!cancelled) setPdfData({ data: new Uint8Array(buf) }); })
      .catch((err) => { if (!cancelled) setLoadError(err); });
    return () => { cancelled = true; };
  }, [file]);

  // Larghezza responsive rispetto al contenitore
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = Math.min(900, Math.max(320, el.clientWidth - 4));
      setPageWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const placements = Array.isArray(value) ? value : [];
  const setPlacements = (next) => onChange && onChange(next);

  const removeIdx = (idx) => setPlacements(placements.filter((_, i) => i !== idx));

  // 🆕 Feb 2026 — Anteprima "fantasma" (page-aware nella modalità scroll continuo)
  const [pendingBox, setPendingBox] = useState(null); // {pageNumber,x0,y0,x1,y1}
  const pendingRef = useRef(null);

  const addAt = (pageNumber, x, y) => {
    const W = 0.25, H = 0.06;
    const cx = Math.max(0, Math.min(1 - W, x - W / 2));
    const cy = Math.max(0, Math.min(1 - H, y - H / 2));
    const next = [...placements, { page: pageNumber, x: +cx.toFixed(4), y: +cy.toFixed(4), w: W, h: H }];
    setPlacements(next);
  };

  const handlePageDown = (pageNumber) => (e) => {
    if (readOnly) return;
    if (dragStateRef.current) return;
    if (e.button !== 0) return;
    const wrapEl = pageWrapRefs.current[pageNumber];
    const rect = wrapEl?.getBoundingClientRect();
    if (!rect) return;
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return;
    e.preventDefault();
    const start = { pageNumber, x0: nx, y0: ny, x1: nx, y1: ny, startClientX: e.clientX, startClientY: e.clientY, moved: false };
    pendingRef.current = start;
    setPendingBox(start);
    const onMove = (ev) => {
      const st = pendingRef.current;
      if (!st) return;
      // Ricalcolo il rect ogni volta (l'utente potrebbe aver scrollato)
      const wrap = pageWrapRefs.current[st.pageNumber];
      const r = wrap?.getBoundingClientRect();
      if (!r) return;
      const nx2 = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
      const ny2 = Math.max(0, Math.min(1, (ev.clientY - r.top) / r.height));
      const dxPx = Math.abs(ev.clientX - st.startClientX);
      const dyPx = Math.abs(ev.clientY - st.startClientY);
      st.moved = st.moved || (dxPx > 3 || dyPx > 3);
      const upd = { ...st, x1: nx2, y1: ny2 };
      pendingRef.current = upd;
      setPendingBox(upd);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const st = pendingRef.current;
      pendingRef.current = null;
      setPendingBox(null);
      if (!st) return;
      const wrap = pageWrapRefs.current[st.pageNumber];
      const r = wrap?.getBoundingClientRect();
      if (!r) return;
      const dxPx = Math.abs((st.x1 - st.x0) * r.width);
      const dyPx = Math.abs((st.y1 - st.y0) * r.height);
      if (!st.moved || dxPx < 8 || dyPx < 8) {
        addAt(st.pageNumber, st.x0, st.y0);
        return;
      }
      const x = Math.min(st.x0, st.x1);
      const y = Math.min(st.y0, st.y1);
      let w = Math.abs(st.x1 - st.x0);
      let h = Math.abs(st.y1 - st.y0);
      w = Math.max(0.05, Math.min(1 - x, w));
      h = Math.max(0.02, Math.min(1 - y, h));
      const next = [...placements, { page: st.pageNumber, x: +x.toFixed(4), y: +y.toFixed(4), w: +w.toFixed(4), h: +h.toFixed(4) }];
      setPlacements(next);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const startDrag = (e, idx, mode) => {
    if (readOnly) return;
    e.stopPropagation();
    e.preventDefault();
    const pnum = parseInt(placements[idx]?.page, 10) || 1;
    const rect = pageWrapRefs.current[pnum]?.getBoundingClientRect();
    if (!rect) return;
    dragStateRef.current = {
      idx, mode, pageNumber: pnum,
      startX: e.clientX, startY: e.clientY,
      pageW: rect.width, pageH: rect.height,
      orig: { ...placements[idx] },
    };
    const onMove = (ev) => {
      const st = dragStateRef.current;
      if (!st) return;
      const dx = (ev.clientX - st.startX) / st.pageW;
      const dy = (ev.clientY - st.startY) / st.pageH;
      const next = [...placements];
      const p = { ...st.orig };
      if (st.mode === "move") {
        p.x = Math.max(0, Math.min(1 - p.w, st.orig.x + dx));
        p.y = Math.max(0, Math.min(1 - p.h, st.orig.y + dy));
      } else if (st.mode === "resize") {
        p.w = Math.max(0.05, Math.min(1 - p.x, st.orig.w + dx));
        p.h = Math.max(0.02, Math.min(1 - p.y, st.orig.h + dy));
      }
      p.x = +p.x.toFixed(4); p.y = +p.y.toFixed(4); p.w = +p.w.toFixed(4); p.h = +p.h.toFixed(4);
      next[st.idx] = p;
      setPlacements(next);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setTimeout(() => { dragStateRef.current = null; }, 50);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const totalAll = placements.length;
  const pagesArr = numPages > 0 ? Array.from({ length: numPages }, (_, i) => i + 1) : [];

  return (
    <div ref={containerRef} className="w-full space-y-2" data-testid={testid}>
      {/* Toolbar (senza Prev/Next: vista scorrevole continua) */}
      <div className="flex items-center justify-between gap-2 rounded-md border bg-slate-50 px-2 py-1.5 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="tabular-nums text-slate-700">
            {numPages > 0 ? <>Vista scorrevole · <strong>{numPages}</strong> pagin{numPages === 1 ? "a" : "e"}</> : "Caricamento…"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Controlli zoom */}
          <div className="inline-flex items-center gap-0.5 border border-slate-300 rounded overflow-hidden bg-white">
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-none"
              disabled={zoom <= 0.5}
              onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
              data-testid={`${testid}-zoom-out`}
              title="Rimpicciolisci"
            >
              <ZoomOut className="h-3.5 w-3.5" />
            </Button>
            <button
              type="button"
              onClick={() => setZoom(1.0)}
              className="text-[10.5px] tabular-nums px-1.5 text-slate-700 hover:bg-slate-100 min-w-[38px]"
              title="Ripristina zoom 100%"
              data-testid={`${testid}-zoom-reset`}
            >
              {Math.round(zoom * 100)}%
            </button>
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-none"
              disabled={zoom >= 2.5}
              onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.1).toFixed(2)))}
              data-testid={`${testid}-zoom-in`}
              title="Ingrandisci"
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-none border-l border-slate-200"
              onClick={() => setZoom(1.0)}
              data-testid={`${testid}-zoom-fit`}
              title="Adatta"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <span className="text-slate-600 flex items-center gap-1">
            <MousePointerClick className="h-3.5 w-3.5 text-indigo-600" />
            {readOnly ? "Anteprima segnaposti" : "Clicca o trascina sul PDF per creare un riquadro"}
          </span>
          <Badge count={totalAll} label="totali" tone="indigo" />
          {!readOnly && totalAll > 0 && (
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:bg-rose-50"
              onClick={() => setPlacements([])}
              data-testid={`${testid}-clear-all`}
              title="Rimuovi tutti i segnaposti"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Svuota
            </Button>
          )}
        </div>
      </div>

      {/* Area PDF scorrevole verticale — tutte le pagine visibili */}
      <div className="w-full bg-slate-100 rounded-md p-2 max-h-[70vh] overflow-auto" data-testid={`${testid}-scroll`}>
        {!file ? (
          <div className="py-16 text-sm text-slate-500 text-center">Carica prima il PDF per posizionare la firma.</div>
        ) : loadError ? (
          <div className="py-16 text-sm text-rose-600 text-center">Impossibile leggere il PDF: {String(loadError?.message || loadError)}</div>
        ) : !pdfData ? (
          <div className="py-16 text-sm text-slate-500 text-center">Caricamento PDF…</div>
        ) : (
          <Document
            file={pdfData}
            options={PDF_OPTIONS}
            onLoadSuccess={({ numPages: n }) => { setNumPages(n); }}
            onLoadError={(err) => { console.warn("PDF placement viewer load error:", err); setLoadError(err); }}
            loading={<div className="py-16 text-sm text-slate-500 text-center">Caricamento PDF…</div>}
            error={<div className="py-16 text-sm text-rose-600 text-center">Impossibile caricare il PDF.</div>}
          >
            <div className="flex flex-col items-center gap-4">
              {pagesArr.map((pnum) => {
                const pagePlacements = placements
                  .map((p, idx) => ({ ...p, __idx: idx }))
                  .filter((p) => (parseInt(p.page, 10) || 1) === pnum);
                return (
                  <div key={pnum} className="w-fit" data-testid={`${testid}-page-${pnum}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 text-center">
                      Pagina {pnum} <span className="text-slate-400">di {numPages}</span>
                      {pagePlacements.length > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0 text-indigo-700">
                          <strong className="tabular-nums">{pagePlacements.length}</strong> segnapost{pagePlacements.length === 1 ? "o" : "i"}
                        </span>
                      )}
                    </div>
                    <div
                      ref={(el) => { pageWrapRefs.current[pnum] = el; }}
                      className={`relative inline-block shadow-md bg-white select-none ${readOnly ? "" : "cursor-crosshair"}`}
                      onPointerDown={handlePageDown(pnum)}
                      data-testid={`${testid}-page-wrap-${pnum}`}
                    >
                      <Page
                        pageNumber={pnum}
                        width={pageWidth * zoom}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                      {pagePlacements.map((p) => (
                        <PlacementBox
                          key={p.__idx}
                          p={p}
                          label={labelFor ? labelFor(p) : "Firma"}
                          onDragStart={(e, mode) => startDrag(e, p.__idx, mode)}
                          onRemove={() => removeIdx(p.__idx)}
                          testid={`${testid}-box-${p.__idx}`}
                          readOnly={readOnly}
                        />
                      ))}
                      {pendingBox && pendingBox.pageNumber === pnum && (
                        <div
                          data-testid={`${testid}-pending`}
                          className="absolute border-2 border-dashed border-indigo-500 bg-indigo-500/25 rounded-sm pointer-events-none"
                          style={{
                            left: `${Math.min(pendingBox.x0, pendingBox.x1) * 100}%`,
                            top: `${Math.min(pendingBox.y0, pendingBox.y1) * 100}%`,
                            width: `${Math.abs(pendingBox.x1 - pendingBox.x0) * 100}%`,
                            height: `${Math.abs(pendingBox.y1 - pendingBox.y0) * 100}%`,
                            opacity: 0.5,
                          }}
                        >
                          <div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-indigo-800 uppercase">
                            Anteprima
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Document>
        )}
      </div>

      <p className="text-[11px] text-slate-500">
        Suggerimento: scorri per navigare tra le pagine. <strong>Trascina</strong> per disegnare un riquadro delle dimensioni desiderate, oppure <strong>clicca</strong> per crearne uno di dimensioni predefinite. Poi trascina il box per spostarlo, la maniglia in basso a destra per ridimensionarlo, la <XIcon className="inline h-3 w-3" /> per rimuoverlo.
      </p>
    </div>
  );
}

function Badge({ count, label, tone = "slate" }) {
  const cls = tone === "indigo"
    ? "bg-indigo-100 text-indigo-800 border-indigo-200"
    : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${cls}`}>
      <strong className="tabular-nums">{count}</strong> {label}
    </span>
  );
}

function PlacementBox({ p, onDragStart, onRemove, testid, label = "Firma", readOnly = false }) {
  const style = {
    left: `${p.x * 100}%`,
    top: `${p.y * 100}%`,
    width: `${p.w * 100}%`,
    height: `${p.h * 100}%`,
  };
  // 🆕 Feb 2026 — Redact = box rosso semi-trasparente (per distinguerlo dai token indigo)
  const isRedact = p.type === "redact";
  const boxCls = isRedact
    ? "absolute border-2 border-rose-500 bg-rose-500/25 rounded-sm select-none group"
    : "absolute border-2 border-indigo-500 bg-indigo-500/15 rounded-sm select-none group";
  const labelCls = isRedact
    ? "absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-rose-800 pointer-events-none uppercase tracking-wide text-center px-1 truncate"
    : "absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-indigo-800 pointer-events-none uppercase tracking-wide text-center px-1 truncate";
  const resizeCls = isRedact
    ? "absolute -bottom-1 -right-1 h-3 w-3 bg-rose-600 rounded-sm cursor-nwse-resize border-2 border-white"
    : "absolute -bottom-1 -right-1 h-3 w-3 bg-indigo-600 rounded-sm cursor-nwse-resize border-2 border-white";
  return (
    <div
      style={style}
      className={boxCls}
      data-testid={testid}
      onPointerDown={readOnly ? undefined : (e) => onDragStart(e, "move")}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={labelCls} title={label}>
        {label}
      </div>
      {!readOnly && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-rose-600 text-white flex items-center justify-center shadow hover:bg-rose-700"
            title="Rimuovi segnaposto"
            data-testid={`${testid}-remove`}
          >
            <XIcon className="h-3 w-3" />
          </button>
          <div
            onPointerDown={(e) => onDragStart(e, "resize")}
            className={resizeCls}
            title="Ridimensiona"
            data-testid={`${testid}-resize`}
          />
        </>
      )}
    </div>
  );
}
